'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { User as UserIcon } from 'lucide-react';
import { getDeterministicAvatarColor } from '@/shared/utils/avatarColors';
import { useTheme } from '../../providers/ThemeProvider';

// Define size mappings once
const SIZE_CONFIG = {
  small: { container: 'w-8 h-8', font: 'text-xs' },       // 32px
  medium: { container: 'w-10 h-10', font: 'text-sm' },    // 40px
  large: { container: 'w-12 h-12', font: 'text-base' },   // 48px
  xlarge: { container: 'w-16 h-16', font: 'text-lg' },   // 64px
  messageAvatar: { container: 'w-10 h-10', font: 'text-sm' }, // Same as medium, kept for compatibility if used
};

type PredefinedSize = keyof typeof SIZE_CONFIG;

interface UserAvatarProps {
  profileImageUrl?: string | null;
  name?: string | null;
  avatarColor?: string | null;
  size?: PredefinedSize | number; // Predefined sizes or custom number for pixels
  className?: string;
  fontSize?: string; // Optional: to control initial's font size explicitly
  showStatus?: boolean;
  isOnline?: boolean;
  priority?: boolean; // For above-fold images - improves LCP
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  profileImageUrl,
  name,
  avatarColor,
  size = 'medium',
  className = '',
  fontSize: explicitFontSize,
  showStatus = false,
  isOnline = false,
  priority = false,
}) => {
  const { isDarkMode } = useTheme();
  const [imageError, setImageError] = useState(false);
  const getInitials = (nameString?: string | null): string => {
    if (!nameString || nameString.trim() === '') return '?';
    const words = nameString.trim().split(/\s+/);
    if (words.length === 1 && words[0].length > 0) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return words
      .map((word) => word[0])
      .filter(char => !!char) // Ensure char is not empty
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'; // Fallback if initials are empty
  };

  const getSizeClassesAndStyles = (): { containerClass: string; fontClass?: string; explicitSizeStyle?: React.CSSProperties, explicitFontStyle?: React.CSSProperties } => {
    if (typeof size === 'number') {
      // For custom number size, direct style manipulation is more robust than JIT w-Xpx/h-Xpx
      return {
        containerClass: 'flex items-center justify-center rounded-full object-cover font-medium', // Base classes
        explicitSizeStyle: { width: `${size}px`, height: `${size}px` },
        explicitFontStyle: { fontSize: explicitFontSize || `${Math.max(10, Math.floor(size / 2.8))}px` }
      };
    }
    const config = SIZE_CONFIG[size] || SIZE_CONFIG.medium;
    return {
      containerClass: `${config.container} flex items-center justify-center rounded-full object-cover font-medium`,
      fontClass: explicitFontSize ? undefined : config.font, // Use explicit font size if provided, else from config
      explicitFontStyle: explicitFontSize ? { fontSize: explicitFontSize } : undefined
    };
  };

  const { containerClass, fontClass, explicitSizeStyle, explicitFontStyle } = getSizeClassesAndStyles();
  const finalContainerClassName = `${containerClass} ${className}`;

  if (profileImageUrl && !imageError) {
    return (
      <div
        className={`relative rounded-full overflow-hidden ${
          typeof size === 'number'
            ? ''
            : SIZE_CONFIG[size as PredefinedSize]?.container || SIZE_CONFIG.medium.container
        } ${className}`}
        style={explicitSizeStyle}
      >
        <Image
          src={profileImageUrl}
          alt={name ? `${name}'s avatar` : 'User avatar'}
          fill
          className="rounded-full object-cover"
          sizes="(max-width: 768px) 32px, (max-width: 1200px) 48px, 64px"
          priority={priority}
          onError={() => setImageError(true)}
        />
        {showStatus && isOnline && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-white"></div>
        )}
      </div>
    );
  }

  const initials = getInitials(name);

  // Get avatar color - use database color or generate deterministic color from name
  const getAvatarColor = (): string => {
    if (avatarColor) {
      return avatarColor;
    }

    // Generate deterministic color based on name using centralized theme colors
    if (name && name.trim() !== '' && name !== '?') {
      return getDeterministicAvatarColor(name, isDarkMode);
    }

    // Light grey fallback for empty/unknown names
    return '#D1D5DB';
  };

  // Detect phone number / unknown contact
  // More comprehensive phone detection:
  // - Starts with optional + or digit
  // - Contains mostly digits with optional formatting characters
  // - Includes common formats: +1234567890, (123) 456-7890, 123-456-7890, etc.
  const phoneRegex = /^[+]?[\d\s\-()./]+$/;
  const hasEnoughDigits = (name || '').replace(/\D/g, '').length >= 7; // At least 7 digits for a valid phone
  const isPhoneLike = name && phoneRegex.test(name) && hasEnoughDigits;

  // Show person icon for:
  // 1. Phone number-like names
  // 2. Names that are "Unknown Contact" or similar
  // 3. Initials that are just digits or '?' (indicates name was a number or unknown)
  const isUnknownContact = name === 'Unknown Contact' || name === 'Unknown' || /^unknown/i.test(name || '');
  const showPersonIcon = isPhoneLike || isUnknownContact || /^\d+$/.test(initials) || initials === '?';

  return (
    <div className="relative">
      <div
        className={finalContainerClassName}
        style={{
          backgroundColor: getAvatarColor(),
          color: 'white',
          ...explicitSizeStyle
        }}
      >
        {showPersonIcon ? (
          <UserIcon size={typeof size === 'number' ? Math.floor(size/1.8) : 16} />
        ) : (
          <span style={explicitFontStyle} className={fontClass}>
            {initials}
          </span>
        )}
      </div>
      {showStatus && isOnline && (
         <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-white"></div>
      )}
    </div>
  );
};

export default UserAvatar;
